import React, { useState, useEffect } from 'react';
import { Head, router } from '@inertiajs/react';
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Rocket,
  Zap,
  ArrowLeft,
  Play,
  Square,
  RefreshCcw,
  Trash2,
  Settings,
  TrendingUp,
  TrendingDown,
  Activity,
  History,
  BarChart3,
  ScrollText,
  Shield
} from "lucide-react";
import { BottomNav, DesktopNav } from '@/components/BottomNav';
import { Header } from '@/components/Header';
import { SignalBotSettings } from '@/components/signal/SignalBotSettings';
import { SignalTradingChart } from '@/components/signal/SignalTradingChart';
import { formatKstTime, formatRemainingHours } from '@/lib/utils';

interface SignalBotShowProps {
  bot: any;
  current_user: any;
}

import { store } from '@/store';
import { Provider } from 'react-redux';

export default function Show(props: SignalBotShowProps) {
  return (
    <Provider store={store}>
      <ShowContent {...props} />
    </Provider>
  );
}

function ShowContent({ bot: initialBot, current_user }: SignalBotShowProps) {
  const [bot, setBot] = useState(initialBot);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // URL hash (#settings)와 isEditing 상태 양방향 동기화
  useEffect(() => {
    const handleHashChange = () => {
      const showSettings = window.location.hash === '#settings';
      setIsEditing(showSettings);
    };

    handleHashChange();
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (isEditing) {
      if (window.location.hash !== '#settings') {
        window.location.hash = 'settings';
      }
    } else {
      if (window.location.hash === '#settings') {
        const newUrl = window.location.pathname + window.location.search;
        window.history.pushState(null, '', newUrl);
      }
    }
  }, [isEditing]);

  const [tempTradingMode, setTempTradingMode] = useState(() => {
    return initialBot.settings?.trading_mode || 'continuous';
  });
  const [tempArmedEntry, setTempArmedEntry] = useState(() => {
    return initialBot.settings?.armed_entry || 'none';
  });
  const [tempArmedEntrySignalType, setTempArmedEntrySignalType] = useState(() => {
    return initialBot.settings?.armed_entry_signal_type || 'default';
  });
  const [tempArmedExit, setTempArmedExit] = useState(() => {
    return !!initialBot.settings?.armed_exit;
  });
  const [tempTpPct, setTempTpPct] = useState(() => {
    const val = initialBot.settings?.tp_pct;
    return val !== undefined && val !== null && val !== '' ? val.toString() : '';
  });
  const [tempSlPct, setTempSlPct] = useState(() => {
    const val = initialBot.settings?.sl_pct;
    return val !== undefined && val !== null && val !== '' ? val.toString() : '';
  });
  const [isTpFocused, setIsTpFocused] = useState(false);
  const [isSlFocused, setIsSlFocused] = useState(false);

  const isControlSettingsDirty =
    tempTradingMode !== (bot.settings?.trading_mode || 'continuous') ||
    tempArmedEntry !== (bot.settings?.armed_entry || 'none') ||
    tempArmedEntrySignalType !== (bot.settings?.armed_entry_signal_type || 'default') ||
    tempArmedExit !== (bot.settings?.armed_exit || false) ||
    (tempTpPct || '').toString() !== (bot.settings?.tp_pct !== undefined ? (bot.settings.tp_pct?.toString() ?? '') : (bot.tp_pct?.toString() ?? '')) ||
    (tempSlPct || '').toString() !== (bot.settings?.sl_pct !== undefined ? (bot.settings.sl_pct?.toString() ?? '') : (bot.sl_pct?.toString() ?? ''));

  useEffect(() => {
    if (!isLoading && !isControlSettingsDirty) {
      setTempTradingMode(bot.settings?.trading_mode || 'continuous');
      setTempArmedEntry(bot.settings?.armed_entry || 'none');
      setTempArmedEntrySignalType(bot.settings?.armed_entry_signal_type || 'default');
      setTempArmedExit(!!bot.settings?.armed_exit);

      const tpVal = bot.settings?.tp_pct;
      if (!isTpFocused) {
        setTempTpPct(tpVal !== undefined && tpVal !== null && tpVal !== '' ? tpVal.toString() : '');
      }

      const slVal = bot.settings?.sl_pct;
      if (!isSlFocused) {
        setTempSlPct(slVal !== undefined && slVal !== null && slVal !== '' ? slVal.toString() : '');
      }
    }
  }, [
    bot.settings?.trading_mode,
    bot.settings?.armed_entry,
    bot.settings?.armed_entry_signal_type,
    bot.strategy_type,
    bot.settings?.tp_pct,
    bot.settings?.sl_pct,
    isLoading
  ]);

  // [New] 페이징 처리용 상태
  const [tradesPage, setTradesPage] = useState(1);
  const [tradesPer, setTradesPer] = useState(20);
  const [tradesData, setTradesData] = useState<{ histories: any[]; total: number; page: number; per: number }>({
    histories: initialBot.trades || [],
    total: initialBot.trades ? initialBot.trades.length : 0,
    page: 1,
    per: 20
  });
  const [isTradesLoading, setIsTradesLoading] = useState(false);

  const [logsData, setLogsData] = useState<{ logs: any[]; total: number }>({ logs: [], total: 0 });
  const [logsPage, setLogsPage] = useState(1);
  const [isLogsLoading, setIsLogsLoading] = useState(false);

  const [scannerLogs, setScannerLogs] = useState<any[]>([]);
  const [isScannerLogsLoading, setIsScannerLogsLoading] = useState(false);

  const [historyTab, setHistoryTab] = useState('trades');

  const fetchLogs = async (page: number) => {
    setIsLogsLoading(true);
    try {
      const res = await fetch(`/signal/logs?bot_id=${bot.id}&page=${page}&per=30`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setLogsData(data);
      }
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setIsLogsLoading(false);
    }
  };

  const fetchScannerLogs = async () => {
    setIsScannerLogsLoading(true);
    try {
      const res = await fetch(`/signal/scanner_logs?symbol=${bot.symbol}`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setScannerLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Failed to fetch scanner logs:', error);
    } finally {
      setIsScannerLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(logsPage);
  }, [bot.id, logsPage]);

  useEffect(() => {
    if (historyTab === 'scanner') {
      fetchScannerLogs();
    }
  }, [historyTab]);

  // [New] 위험 구역 접기/펴기 상태 (기본 접힘)
  const [isDangerZoneOpen, setIsDangerZoneOpen] = useState(false);

  // 매매 이력 페이징 데이터 로드
  const fetchTradesHistory = async (page: number, per: number) => {
    setIsTradesLoading(true);
    try {
      const res = await fetch(`/signal/trades_history?bot_id=${bot.id}&page=${page}&per=${per}&include_entries=true`, {
        headers: { 'Accept': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        setTradesData(data);
      }
    } catch (error) {
      console.error('Failed to fetch trades history:', error);
    } finally {
      setIsTradesLoading(false);
    }
  };

  useEffect(() => {
    fetchTradesHistory(tradesPage, tradesPer);
  }, [bot.id, tradesPage, tradesPer]);

  // 실시간 데이터 동기화
  useEffect(() => {
    if (isEditing || isControlSettingsDirty) return; // 사용자가 설정 수정 중이거나 제어 값 변경 중에는 폴링 중단 (값 되돌림 방지)
    const timer = setInterval(async () => {
      try {
        const response = await fetch(`/signal/${bot.id}`, {
          headers: {
            'Accept': 'application/json',
            'X-Polling': 'true'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.bot) {
            setBot(data.bot);
            // 1페이지를 보고 있을 때는 폴링 시 매매 이력도 실시간 갱신
            if (tradesPage === 1) {
              fetchTradesHistory(1, tradesPer);
            }
            if (logsPage === 1) {
              fetchLogs(1);
            }
            if (historyTab === 'scanner') {
              fetchScannerLogs();
            }
          }
        }
      } catch (error) {
        console.error('Polling failed:', error);
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [bot.id, isEditing, tradesPage, tradesPer, logsPage, historyTab]);

  const handleAction = async (action: string) => {
    if (action === 'start' && bot.source === 'auto_scanner') {
      const confirmed = window.confirm(
        "⚠️ 이 봇은 자율형 스캐너(Auto Scanner) 봇입니다.\n\n" +
        "사용자가 수동으로 가동을 시작하더라도, 백그라운드 스캐너가 새로운 타점 신호를 감지하여 봇을 업데이트/시작하기 전까지는 매매 로직이 무시되거나 대기할 수 있습니다.\n\n" +
        "정말로 시작하시겠습니까?"
      );
      if (!confirmed) return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`/signal/${bot.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        }
      });
      const data = await response.json();
      if (data.bot) setBot(data.bot);
    } catch (error) {
      console.error(`${action} failed:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = () => {
    if (confirm('정말로 이 봇을 삭제하시겠습니까?')) {
      router.delete(`/signal/${bot.id}`);
    }
  };

  const handleSaveSettings = () => {
    setIsEditing(false);
  };

  const handleModeChange = async (mode: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/signal/${bot.id}/set_trading_mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        },
        body: JSON.stringify({ mode })
      });
      const data = await response.json();
      if (data.bot) setBot(data.bot);
    } catch (error) {
      console.error('Mode change failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshBot = async () => {
    try {
      const response = await fetch(`/signal/${bot.id}`, {
        headers: {
          'Accept': 'application/json',
          'X-Polling': 'true'
        }
      });
      if (!response.ok) return;
      const data = await response.json();
      if (data.bot) setBot(data.bot);
    } catch (error) {
      console.error('Failed to refresh bot:', error);
    }
  };

  const handleManualAction = async (action: string, params: any = {}) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/signal/${bot.id}/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        },
        body: JSON.stringify(params)
      });
      const data = await response.json();
      if (data.bot) setBot(data.bot);
      window.setTimeout(() => {
        refreshBot();
      }, 1200);
    } catch (error) {
      console.error(`${action} failed:`, error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArmEntry = async (mode: string) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/signal/${bot.id}/arm_entry`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        },
        body: JSON.stringify({ mode, armed_entry_signal_type: tempArmedEntrySignalType === 'default' ? null : tempArmedEntrySignalType })
      });
      const data = await response.json();
      if (data.bot) setBot(data.bot);
    } catch (error) {
      console.error('Arm entry failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleArmExit = async (enabled: boolean) => {
    setIsLoading(true);
    try {
      const response = await fetch(`/signal/${bot.id}/arm_exit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        },
        body: JSON.stringify({ enabled })
      });
      const data = await response.json();
      if (data.bot) setBot(data.bot);
    } catch (error) {
      console.error('Arm exit failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveControlSettings = async () => {
    setIsLoading(true);
    try {
      const updatedSettings = {
        ...bot.settings,
        trading_mode: tempTradingMode,
        armed_entry: tempArmedEntry,
        armed_entry_signal_type: tempArmedEntrySignalType === 'default' ? null : tempArmedEntrySignalType,
        armed_exit: tempArmedExit,
        tp_pct: tempTpPct !== '' ? Number(tempTpPct) : null,
        sl_pct: tempSlPct !== '' ? Number(tempSlPct) : null
      };

      const response = await fetch(`/signal/${bot.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content
        },
        body: JSON.stringify({ bot: { settings: updatedSettings } })
      });
      const data = await response.json();
      if (data.bot) {
        setBot(data.bot);
      }
    } catch (error) {
      console.error('Failed to save control settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground pb-24">
      <Head title={`${bot.name} - Signal Bot`} />

      <div className="max-w-7xl mx-auto px-4 pt-4 md:pt-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-6">
          <Header />
          <div className="flex items-center gap-2">
            <DesktopNav activeTab="bots" setActiveTab={(tab) => {
              if (tab === 'dca') window.location.href = '/dca';
              else window.location.href = `/signal#${tab}`;
            }} current_user={current_user} />
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* 상단 네비게이션 & 제목 */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => router.get('/signal')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <div className="flex items-center gap-2 mb-1">
                <Badge variant="outline" className="text-[10px] font-black uppercase">{bot.exchange}</Badge>
                <Badge className={`text-[10px] font-black uppercase ${!bot.is_live ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                  bot.use_testnet ? 'bg-primary/10 text-primary border-primary/20' :
                    'bg-success/10 text-success border-success/20'
                  }`}>
                  {!bot.is_live ? 'Simulation' : bot.use_testnet ? 'Testnet' : 'Live'}
                </Badge>
                {bot.is_active && (
                  <Badge className="bg-primary text-primary-foreground animate-pulse text-[10px] font-black">RUNNING</Badge>
                )}
              </div>
              <h1 className="text-2xl font-black tracking-tight">{bot.name}</h1>
            </div>
          </div>

          <div className="flex items-center gap-1.5 sm:gap-2">
            {bot.is_active && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (confirm('현재 포지션을 즉시 시장가로 청산하고 봇을 중지하시겠습니까?')) {
                    handleAction('liquidate');
                  }
                }}
                disabled={isLoading}
                className="font-black gap-1 rounded-xl h-9 sm:h-10 px-3 sm:px-4 border-danger/50 text-danger hover:bg-danger/5 text-xs sm:text-sm"
              >
                <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 fill-danger" /> 시장가 전액 청산
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="font-bold gap-1 rounded-xl h-9 sm:h-10 px-3 sm:px-4 text-xs sm:text-sm"
            >
              <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 설정 수정
            </Button>
            <Button
              variant={bot.is_active ? "destructive" : "default"}
              size="sm"
              onClick={() => handleAction(bot.is_active ? 'stop' : 'start')}
              disabled={isLoading}
              className={`font-black gap-1 rounded-xl h-9 sm:h-10 px-4 sm:px-6 shadow-md text-xs sm:text-sm ${!bot.is_active && 'bg-primary hover:scale-105 transition-transform'}`}
            >
              {bot.is_active ? <><Square className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 중지</> : <><Play className="w-3.5 h-3.5 sm:w-4 sm:h-4" /> 가동 시작</>}
            </Button>
          </div>
        </div>

        {isEditing ? (
          <>
            <SignalBotSettings
              botData={bot}
              setBotData={setBot}
              enabledExchanges={{ [bot.exchange]: true }}
              onSave={handleSaveSettings}
              onCancel={() => setIsEditing(false)}
            />
          </>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* 요약 카드 영역 */}
            <div className="lg:col-span-1 space-y-6">
              <Card className="border-border/50 shadow-sm bg-card overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border/50 p-4">
                  <CardTitle className="text-sm font-black flex items-center gap-2">
                    <Activity className="w-4 h-4 text-primary" />
                    현재 상태 요약
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-4">
                  <div className="flex justify-between items-end">
                    <span className="text-xs font-bold text-muted-foreground uppercase">현재 가격</span>
                    <span className="text-xl font-black">{(bot.current_price || 0).toLocaleString()} <span className="text-xs font-bold">USDT</span></span>
                  </div>

                  {/* 누적 수익 섹션 */}
                  <div className="pt-4 border-t border-border/30 space-y-2">
                    <div className="flex justify-between items-end">
                      <span className="text-xs font-black text-foreground uppercase">누적 수익 (Net PnL)</span>
                      <span className={`text-lg font-black ${(bot.net_pnl || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                        {(bot.net_pnl || 0) >= 0 ? '+' : ''}{Number(bot.net_pnl || 0).toFixed(4)} USDT
                      </span>
                    </div>
                    <div className="flex justify-between text-[11px] text-muted-foreground font-medium">
                      <span>총 실현 손익 (Gross)</span>
                      <span>{Number(bot.total_pnl || 0).toFixed(4)} USDT</span>
                    </div>
                    <div className="flex justify-between text-[11px] text-muted-foreground font-medium">
                      <span>총 누적 수수료 (Fees)</span>
                      <span>{Number(bot.total_fees || 0).toFixed(4)} USDT</span>
                    </div>
                  </div>

                  {/* 생성 시각 */}
                  {bot.created_at && (
                    <div className="flex justify-between items-center text-[11px] pt-3 border-t border-border/30">
                      <span className="text-muted-foreground font-bold">봇 생성 시각</span>
                      <span className="font-black">{formatKstTime(bot.created_at)}</span>
                    </div>
                  )}

                  {/* 시작 시간 */}
                  {bot.last_started_at && (
                    <div className="flex justify-between items-center text-[11px] pt-3 border-t border-border/30">
                      <span className="text-muted-foreground font-bold">시작 시간</span>
                      <span className="font-black">{formatKstTime(bot.last_started_at)}</span>
                    </div>
                  )}

                  {/* 자동 정지 타이머 표시 */}
                  {bot.is_active && bot.settings?.use_auto_stop && bot.last_started_at && (
                    <div className="bg-amber-500/5 p-3 rounded-2xl border border-amber-500/20 mt-2">
                      <div className="flex justify-between items-center text-[11px] mb-1.5">
                        <span className="text-amber-600 font-black flex items-center gap-1.5">
                          <Rocket className="w-3.5 h-3.5" /> 남은 가동 시간
                        </span>
                        <span className="text-amber-700 font-black">{formatRemainingHours(bot.last_started_at)}</span>
                      </div>
                      <Button
                        onClick={() => handleAction('extend')}
                        variant="ghost"
                        size="sm"
                        className="w-full font-black text-[10px] h-8 text-amber-600 hover:bg-amber-500/10 border border-amber-500/20 rounded-xl"
                      >
                        <RefreshCcw className="w-3 h-3 mr-1" /> 지금부터 24시간 연장
                      </Button>
                    </div>
                  )}

                  <div className="pt-4 border-t border-border/30 space-y-3">
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-bold">종목</span>
                      <span className="font-black">{bot.symbol}</span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-bold">전략</span>
                      <span className="font-black uppercase">{bot.strategy_type === 'hull_macd' ? 'Hull MACD' : bot.strategy_type === 'ma_angle_breakout' ? 'MA Angle Breakout' : bot.strategy_type === 'auto4_signal' ? 'Auto4 Signal' : bot.strategy_type}</span>
                    </div>
                    <div className="flex justify-between text-xs items-start gap-2 pt-1">
                      <span className="text-muted-foreground font-bold shrink-0">전략 상태</span>
                      <span className={`font-bold text-right leading-tight ${bot.cooldown_remaining > 0 ? 'text-red-500 animate-pulse font-black' : 'text-primary'}`}>
                        {bot.cooldown_remaining > 0 
                          ? `⚠️ 수동 청산 쿨다운 중 (${bot.cooldown_remaining}초 남음)` 
                          : (bot.status || '상태 대기 중...')}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-bold">봉 간격</span>
                      <span className="font-black">{bot.interval}</span>
                    </div>

                    {/* 현재 포지션 표시 영역 */}
                    <div className="pt-4 border-t border-border/30 space-y-3">
                      <div className="flex justify-between items-center text-xs">
                        <span className="text-muted-foreground font-bold">현재 포지션</span>
                        {bot.position && bot.position.side ? (
                          <Badge className={`font-black uppercase px-2.5 py-1 ${bot.position.side === 'long' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30' : 'bg-rose-500/10 text-rose-500 border border-rose-500/30'}`}>
                            {(() => {
                              const qty = Number(bot.position.qty || 0);
                              const entry = Number(bot.position.entry_price || 0);
                              const lev = Number(bot.leverage || 1);
                              const position_value = qty > 0 && entry > 0 ? qty * entry : 0;
                              const margin = position_value > 0 ? position_value / lev : 0;
                              return position_value > 0
                                ? `${bot.position.side.toUpperCase()} | Pos $${position_value.toLocaleString(undefined, { maximumFractionDigits: 2 })} / Margin $${margin.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                                : `${bot.position.side.toUpperCase()} (—)`;
                            })()}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground font-bold">포지션 없음 (대기 중)</span>
                        )}
                      </div>
                      {bot.position && bot.position.side && bot.position.entry_price > 0 && (
                        <>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground font-bold">현재가</span>
                            <span className="font-black">{Number(bot.current_price || 0).toLocaleString(undefined, { maximumFractionDigits: 8 })} USDT</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-muted-foreground font-bold">평균 진입가</span>
                            <span className="font-black">{Number(bot.position.entry_price).toLocaleString(undefined, { maximumFractionDigits: 8 })} USDT</span>
                          </div>
                          <div className="flex justify-between text-xs items-center">
                            <span className="text-muted-foreground font-bold">미실현 수익</span>
                            {(() => {
                              const entry = Number(bot.position.entry_price);
                              const cur = Number(bot.current_price || 0);
                              const qty = Number(bot.position.qty || 0);
                              if (entry <= 0 || cur <= 0) return <span className="font-bold">-</span>;
                              const pctChange = bot.position.side === 'long' ? ((cur - entry) / entry) * 100 : ((entry - cur) / entry) * 100;
                              const pnlUsdt = bot.position.side === 'long' ? (cur - entry) * qty : (entry - cur) * qty;
                              const leverage = Number(bot.leverage || 1);
                              const roePct = pctChange * leverage;
                              const color = pctChange >= 0 ? 'text-emerald-500' : 'text-rose-500';
                              return (
                                <div className="flex items-center gap-2">
                                  <span className={`font-black ${color}`}>
                                    {pctChange >= 0 ? '+' : ''}{pctChange.toFixed(2)}%
                                  </span>
                                  <span className="text-[10px] font-bold text-muted-foreground/70">
                                    ROE {roePct >= 0 ? '+' : ''}{roePct.toFixed(2)}%
                                  </span>
                                  <span className={`font-bold text-xs ${color}`}>
                                    ({pnlUsdt >= 0 ? '+' : ''}{pnlUsdt.toFixed(4)} USDT)
                                  </span>
                                </div>
                              );
                            })()}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex justify-between text-xs">
                      <span className="text-muted-foreground font-bold">레버리지</span>
                      <span className="font-black">x{bot.leverage}</span>
                    </div>
                  </div>

                  <div className="pt-4 border-t border-border/30 grid grid-cols-2 gap-4">
                    <div className="bg-success/5 p-3 rounded-xl border border-success/10">
                      <span className="text-[10px] font-black text-success uppercase block mb-1">Target TP</span>
                      <span className="text-sm font-black">
                        {bot.settings?.tp_pct !== undefined && bot.settings.tp_pct !== null && bot.settings.tp_pct !== '' ? `${bot.settings.tp_pct}%` : bot.tp_pct ? `${bot.tp_pct}%` : '미설정 (기본 2.0%)'}
                      </span>
                    </div>
                    <div className="bg-danger/5 p-3 rounded-xl border border-danger/10">
                      <span className="text-[10px] font-black text-danger uppercase block mb-1">Stop Loss</span>
                      <span className="text-sm font-black">
                        {bot.settings?.sl_pct !== undefined && bot.settings.sl_pct !== null && bot.settings.sl_pct !== '' ? `${bot.settings.sl_pct}%` : bot.sl_pct ? `${bot.sl_pct}%` : '미설정 (기본 1.0%)'}
                      </span>
                    </div>
                  </div>

                  {bot.settings?.use_gradual_tp !== false && (
                    <div className="pt-3 border-t border-border/30 space-y-2">
                      <span className="text-[10px] font-black text-success uppercase block">점진적 분할 익절 진행 상황</span>
                      <div className="grid grid-cols-3 gap-2">
                        {((bot.position?.tp_targets as any[]) || []).slice(0, 3).map((t: any, i: number) => (
                          <div key={i} className={`p-2 rounded-lg border text-xs font-bold ${t.executed ? 'bg-success/10 border-success/30 text-success' : 'bg-muted/30 border-border/50 text-muted-foreground'}`}>
                            <span className="block text-[9px] uppercase">{i + 1}차 ({t.pct}%)</span>
                            <span className="block">{t.executed ? '✅ 익절 완료' : `⏳ ${t.price}`}</span>
                          </div>
                        ))}
                      </div>
                      {bot.position?.breakeven_activated && (
                        <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                          <span className="text-[10px] font-black text-amber-500">🛡️ 본절 방어 활성화</span>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* [New] 반자동 / 수동 제어 패널 */}
              <Card className="border-border/50 shadow-sm bg-card overflow-hidden">
                <CardHeader className="bg-muted/30 border-b border-border/50 p-4 flex flex-row items-center justify-between">
                  <CardTitle className="text-sm font-bold flex items-center gap-2 text-foreground/80">
                    <Settings className="w-4 h-4 text-muted-foreground" />
                    수동 및 예약 매매 제어
                  </CardTitle>
                  <Badge variant="secondary" className="text-[10px] font-bold border border-border/50">
                    {tempTradingMode === 'semi_auto' ? '예약/수동 모드' : '상시 자동 모드'}
                  </Badge>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                  {/* 모드 선택 토글 버튼 */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <label className="text-xs font-bold text-muted-foreground block">매매 모드 선택</label>
                      {tempTradingMode !== (bot.settings?.trading_mode || 'continuous') && (
                        <span className="text-[9px] font-black text-amber-500 uppercase animate-pulse">저장 대기 중</span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 bg-muted/50 p-1 rounded-2xl border border-border/50">
                      <Button
                        variant={tempTradingMode !== 'semi_auto' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setTempTradingMode('continuous')}
                        disabled={isLoading}
                        className={`rounded-xl font-bold text-xs h-9 shadow-none transition-all ${tempTradingMode !== 'semi_auto'
                          ? 'bg-primary/15 text-primary border border-primary/25 shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                          }`}
                      >
                        상시 자동 매매
                      </Button>
                      <Button
                        variant={tempTradingMode === 'semi_auto' ? 'secondary' : 'ghost'}
                        size="sm"
                        onClick={() => setTempTradingMode('semi_auto')}
                        disabled={isLoading}
                        className={`rounded-xl font-bold text-xs h-9 shadow-none transition-all ${tempTradingMode === 'semi_auto'
                          ? 'bg-primary/15 text-primary border border-primary/25 shadow-sm'
                          : 'text-muted-foreground hover:text-foreground'
                          }`}
                      >
                        예약 / 수동 매매
                      </Button>
                    </div>
                    <p className="text-[11px] text-muted-foreground leading-relaxed pt-1 px-1">
                      {tempTradingMode === 'semi_auto'
                        ? '지표 신호에 상시 진입하지 않고, 수동 진입 또는 1회성 예약 진입/청산만 수행합니다.'
                        : '지표 신호가 발생할 때마다 자동으로 무한 반복 진입 및 청산을 수행합니다.'}
                    </p>

                    <div className="pt-2 pb-1 space-y-1.5 border-t border-border/10 mt-2">
                      <div className="flex justify-between items-center">
                        <label className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider block">🎯 적용 신호 지표</label>
                        <div className="flex items-center gap-1.5">
                          {tempArmedEntrySignalType !== (bot.settings?.armed_entry_signal_type || 'default') && (
                            <span className="text-[9px] font-black text-amber-500 uppercase animate-pulse">저장 대기 중</span>
                          )}
                          <Badge variant="outline" className="text-[9px] font-medium border-border text-muted-foreground">
                            {(() => {
                              const type = tempArmedEntrySignalType;
                              return type === 'default' ? '전략 기본' : type === 'zerolag_macd' ? 'Zl Macd' : type === 'macd_ema' ? 'Macd' : type === 'hull_macd' ? 'Hull Macd' : type;
                            })()}
                          </Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-1 bg-muted/20 p-0.5 rounded-xl border border-border/30">
                        {[
                          { value: 'default', label: '전략 기본' },
                          { value: 'zerolag_macd', label: 'Zl Macd' },
                          { value: 'macd_ema', label: 'Macd' },
                          { value: 'hull_macd', label: 'Hull Macd' }
                        ].map((opt) => (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => setTempArmedEntrySignalType(opt.value)}
                            disabled={isLoading}
                            className={`rounded-lg font-bold text-[10px] h-7 transition-all ${tempArmedEntrySignalType === opt.value
                              ? 'bg-primary/15 text-primary border border-primary/20 shadow-sm'
                              : 'text-muted-foreground hover:text-foreground hover:bg-muted/10'
                              }`}
                          >
                            {opt.label}
                          </button>
                        ))}
                      </div>
                      <p className="text-[10px] text-muted-foreground leading-relaxed px-1">
                        선택한 지표를 상시 자동 매매와 예약 진입 모두에 공통 적용합니다.
                      </p>
                    </div>
                  </div>

                  {/* 1. 진입 (매수) 영역 */}
                  <div className="space-y-4 pt-4 border-t border-border/30">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-foreground/80">
                      <span className="flex h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
                      신규 진입 (매수) 영역
                    </div>

                    {/* 즉시 진입 */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-muted-foreground block">⚡ 즉시 진입 (수동 즉시 매수)</label>
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleManualAction('manual_entry', { side: 'long' })}
                          disabled={isLoading || !!bot.position?.side}
                          className="rounded-xl font-bold text-xs h-10 transition-colors bg-success/10 border-success/30 text-success hover:bg-success/20 disabled:opacity-40 disabled:bg-muted/10 disabled:text-muted-foreground disabled:border-transparent"
                        >
                          즉시 Long 진입
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleManualAction('manual_entry', { side: 'short' })}
                          disabled={isLoading || !!bot.position?.side}
                          className="rounded-xl font-bold text-xs h-10 transition-colors bg-danger/10 border-danger/30 text-danger hover:bg-danger/20 disabled:opacity-40 disabled:bg-muted/10 disabled:text-muted-foreground disabled:border-transparent"
                        >
                          즉시 Short 진입
                        </Button>
                      </div>
                    </div>

                    {/* 예약 진입 */}
                    {tempTradingMode === 'semi_auto' && (
                      <div className="space-y-1.5 pt-1">
                        <div className="flex justify-between items-center">
                          <label className="text-[11px] font-bold text-muted-foreground block">⏰ 예약 진입 (신호 대기 후 1회 진입)</label>
                          <div className="flex items-center gap-1.5">
                            {tempArmedEntry !== (bot.settings?.armed_entry || 'none') && (
                              <span className="text-[9px] font-black text-amber-500 uppercase animate-pulse">저장 대기 중</span>
                            )}
                            <Badge variant="outline" className="text-[10px] font-medium uppercase border-border text-muted-foreground">
                              현재: {tempArmedEntry}
                            </Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-1.5 bg-muted/30 p-1 rounded-2xl border border-border/50">
                          {(['none', 'auto', 'long', 'short'] as const).map((mode) => (
                            <Button
                              key={mode}
                              variant={tempArmedEntry === mode ? 'secondary' : 'ghost'}
                              size="sm"
                              onClick={() => setTempArmedEntry(mode)}
                              disabled={isLoading}
                              className={`rounded-xl font-bold text-[11px] h-8 shadow-none capitalize transition-all ${tempArmedEntry === mode
                                ? mode === 'none'
                                  ? 'bg-background text-foreground border border-border/40 shadow-sm'
                                  : 'bg-success/15 text-success border border-success/25 shadow-sm'
                                : 'text-muted-foreground hover:text-foreground'
                                }`}
                            >
                              {mode === 'none' ? '예약 해제' : `${mode} 예약`}
                            </Button>
                          ))}
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-relaxed px-1">
                          설정된 방향의 지표 신호(MACD/MA 등) 발생 시 딱 1회 자동 진입 후 해제됩니다.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 2. 매도 (청산) 영역 */}
                  <div className="space-y-4 pt-4 border-t border-border/30">
                    <div className="flex items-center gap-1.5 text-xs font-bold text-foreground/80">
                      <span className="flex h-1.5 w-1.5 rounded-full bg-muted-foreground/60" />
                      포지션 매도 (청산) 영역
                    </div>

                    {/* 즉시 매도 */}
                    <div className="space-y-1.5">
                      <label className="text-[11px] font-bold text-muted-foreground block">⚡ 즉시 매도 (수동 시장가 청산)</label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleManualAction('manual_close')}
                        disabled={isLoading || !bot.is_active || !bot.position?.side}
                        className="w-full rounded-xl font-bold text-xs h-10 transition-colors bg-danger/10 border-danger/30 text-danger hover:bg-danger/20 disabled:opacity-40 disabled:bg-muted/10 disabled:text-muted-foreground disabled:border-primary/30"
                      >
                        시장가 즉시 매도 (전액 청산)
                      </Button>
                    </div>

                    {/* 예약 매도 */}
                    {tempTradingMode === 'semi_auto' && (
                      <div className="space-y-1.5 pt-1">
                        <div className="flex justify-between items-center">
                          <label className="text-[11px] font-bold text-muted-foreground block">⏰ 예약 매도 (신호 대기 후 1회 청산)</label>
                          <div className="flex items-center gap-1.5">
                            {tempArmedExit !== (bot.settings?.armed_exit || false) && (
                              <span className="text-[9px] font-black text-amber-500 uppercase animate-pulse">저장 대기 중</span>
                            )}
                            <Badge variant="outline" className="text-[10px] font-medium border-border text-muted-foreground">
                              {tempArmedExit ? 'ON (동작 중)' : 'OFF (해제됨)'}
                            </Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 bg-muted/30 p-1 rounded-2xl border border-border/50">
                          <Button
                            variant={tempArmedExit ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setTempArmedExit(true)}
                            disabled={isLoading}
                            className={`rounded-xl font-bold text-xs h-8 shadow-none transition-all ${tempArmedExit
                              ? 'bg-warning/15 text-warning border border-warning/25 shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                              }`}
                          >
                            예약 매도 ON
                          </Button>
                          <Button
                            variant={!tempArmedExit ? 'secondary' : 'ghost'}
                            size="sm"
                            onClick={() => setTempArmedExit(false)}
                            disabled={isLoading}
                            className={`rounded-xl font-bold text-xs h-8 shadow-none transition-all ${!tempArmedExit
                              ? 'bg-background text-foreground border border-border/40 shadow-sm'
                              : 'text-muted-foreground hover:text-foreground'
                              }`}
                          >
                            예약 매도 OFF
                          </Button>
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-relaxed px-1">
                          포지션 보유 중 지표 역전 또는 청산 신호 발생 시 자동 매도(청산) 후 해제됩니다.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* [신규] 자동 청산 조건 설정 */}
                  <div className="space-y-3 pt-4 border-t border-border/30">
                    <div className="flex justify-between items-center">
                      <label className="text-[11px] font-black text-muted-foreground uppercase tracking-wider block">🛡️ 자동 청산 조건 (익절/손절 %)</label>
                      {(tempTpPct.toString() !== (bot.settings?.tp_pct || '').toString() ||
                        tempSlPct.toString() !== (bot.settings?.sl_pct || '').toString()) && (
                          <span className="text-[9px] font-black text-amber-500 uppercase animate-pulse">저장 대기 중</span>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-muted-foreground font-bold">익절 비율 (Target TP %)</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="예: 1.5"
                          value={tempTpPct}
                          onChange={(e) => setTempTpPct(e.target.value)}
                          onFocus={() => setIsTpFocused(true)}
                          onBlur={() => setIsTpFocused(false)}
                          className="w-full bg-muted/30 border border-border/50 rounded-xl px-3 py-1.5 text-xs font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
                        />
                        <p className="text-[9px] text-muted-foreground/80 font-semibold pl-1 mt-0.5">
                          기존 설정: {bot.settings?.tp_pct !== undefined && bot.settings.tp_pct !== null && bot.settings.tp_pct !== '' ? `${bot.settings.tp_pct}%` : '미설정 (기본 2.0%)'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <div className="flex justify-between items-center">
                          <span className="text-[10px] text-muted-foreground font-bold">손절 비율 (Stop Loss %)</span>
                        </div>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="예: 1.0"
                          value={tempSlPct}
                          onChange={(e) => setTempSlPct(e.target.value)}
                          onFocus={() => setIsSlFocused(true)}
                          onBlur={() => setIsSlFocused(false)}
                          className="w-full bg-muted/30 border border-border/50 rounded-xl px-3 py-1.5 text-xs font-bold text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
                        />
                        <p className="text-[9px] text-muted-foreground/80 font-semibold pl-1 mt-0.5">
                          기존 설정: {bot.settings?.sl_pct !== undefined && bot.settings.sl_pct !== null && bot.settings.sl_pct !== '' ? `${bot.settings.sl_pct}%` : '미설정 (기본 1.0%)'}
                        </p>
                      </div>
                    </div>
                    <p className="text-[10px] text-amber-500 font-bold leading-relaxed px-1">
                      ⚠️ [실시간 감시 전역 청산] : 지표 연산이나 캔들 마감과 관계없이 매 틱(2초)마다 감시하여 즉시 종료하는 하드(Hard) 안전장치입니다. (전략 설정 내의 동적 TP/SL보다 먼저 발동)
                    </p>
                    {bot.strategy_type === 'kewma' && (
                      <div className="bg-muted/50 p-2.5 rounded-xl border border-border/50 text-[10px] mt-1">
                        <p className="text-[9px] text-muted-foreground leading-relaxed">
                          💡 현재 KEWMA 전략 개별 설정값 — 익절(TP ROE): {(bot.settings?.tp_roe ? `${bot.settings.tp_roe * 100}%` : '30%')} / 손절(SL ROE): {(bot.settings?.sl_roe ? `${bot.settings.sl_roe * 100}%` : '-30%')}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* 제어 설정 일괄 저장 버튼 */}
                  <div className="pt-4 border-t border-border/30">
                    <Button
                      onClick={handleSaveControlSettings}
                      disabled={isLoading || !isControlSettingsDirty}
                      className={`w-full h-9 rounded-xl font-bold text-xs transition-all flex items-center justify-center gap-2 ${isControlSettingsDirty
                        ? 'bg-primary/20 hover:bg-primary/30 text-primary border border-primary/30 shadow-sm'
                        : 'bg-muted/10 text-muted-foreground/40 cursor-not-allowed border border-primary/30'
                        }`}
                    >
                      <Settings className="w-3.5 h-3.5" />
                      제어 설정 저장
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/50 shadow-sm bg-card overflow-hidden">
                <CardHeader
                  className="bg-muted/30 p-4 cursor-pointer hover:bg-muted/50 transition-colors flex flex-row items-center justify-between select-none"
                  onClick={() => setIsDangerZoneOpen(!isDangerZoneOpen)}
                >
                  <CardTitle className="text-sm font-black flex items-center gap-2 text-danger">
                    <Trash2 className="w-4 h-4" />
                    위험 구역 (Danger Zone)
                  </CardTitle>
                  <Button variant="ghost" size="sm" className="h-6 px-2 text-[10px] font-bold text-muted-foreground hover:text-foreground">
                    {isDangerZoneOpen ? '접기 ▲' : '펼치기 ▼'}
                  </Button>
                </CardHeader>
                {isDangerZoneOpen && (
                  <CardContent className="p-6 space-y-4 border-t border-border/50 bg-danger/5">
                    <p className="text-[11px] text-danger-foreground/80 font-bold leading-relaxed">
                      ⚠️ 봇의 내부 상태를 강제 초기화하거나 영구히 삭제할 수 있습니다. 삭제된 데이터는 절대 복구할 수 없습니다.
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <Button variant="outline" size="sm" onClick={() => handleAction('reset')} className="font-bold border-danger/30 text-danger hover:bg-danger/10">상태 리셋</Button>
                      <Button variant="destructive" size="sm" onClick={handleDelete} className="font-black shadow-md hover:bg-danger/90">봇 삭제</Button>
                    </div>
                  </CardContent>
                )}
              </Card>
            </div>

            {/* 메인 영역 (차트 및 이력) */}
            <div className="lg:col-span-2 space-y-6">
              <SignalTradingChart
                symbol={bot.symbol}
                exchange={bot.exchange}
                interval={bot.interval}
                isTestnet={bot.use_testnet}
                strategyType={bot.strategy_type}
                trades={bot.trades}
                position={bot.position}
              />

              <Card className="border-border/50 shadow-sm bg-card overflow-hidden">
                <Tabs value={historyTab} onValueChange={setHistoryTab}>
                  <TabsList className="w-full rounded-none border-b border-border bg-transparent p-0 h-auto flex">
                    <TabsTrigger value="trades" className="flex-1 py-3 text-[10px] font-bold data-[state=active]:text-primary">
                      매매 이력 ({tradesData.total.toLocaleString()}건)
                    </TabsTrigger>
                    <TabsTrigger value="logs" className="flex-1 py-3 text-[10px] font-bold data-[state=active]:text-primary">
                      봇 로그 ({logsData.total.toLocaleString()}건)
                    </TabsTrigger>
                    <TabsTrigger value="scanner" className="flex-1 py-3 text-[10px] font-bold data-[state=active]:text-primary">
                      스캐너 매치 이력
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="trades" className="mt-0">
                    <CardContent className="p-0">
                      {isTradesLoading && tradesData.histories.length === 0 ? (
                        <div className="flex justify-center items-center py-16">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : tradesData.histories && tradesData.histories.length > 0 ? (
                        <div className="space-y-4 p-4">
                          <div className={`overflow-x-auto max-h-[500px] overflow-y-auto border border-border/50 rounded-xl transition-opacity duration-200 ${isTradesLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                            <table className="w-full text-left border-collapse">
                              <thead>
                                <tr className="border-b border-border/50 text-[10px] font-black text-muted-foreground uppercase bg-muted/30 sticky top-0 backdrop-blur">
                                  <th className="p-3 pl-4">시간</th>
                                  <th className="p-3">종류 / 방향</th>
                                  <th className="p-3 text-right">체결 가격(USDT)</th>
                                  <th className="p-3 text-right">수량</th>
                                  <th className="p-3 text-right">총 손익 (USDT)<br/>Roe%/변화%</th>
                                  <th className="p-3 text-right">수수료 (Fee)</th>
                                  <th className="p-3 pr-4">사유</th>
                                </tr>
                              </thead>
                              <tbody className="text-xs divide-y divide-border/30 font-normal">
                                {tradesData.histories.map((trade: any) => {
                                  const isLong = trade.side === 'long' || trade.side?.toLowerCase() === 'long';
                                  const isEntry = trade.type?.startsWith('entry');
                                  return (
                                    <tr key={trade.id} className="hover:bg-muted/20 transition-colors">
                                      <td className="p-3 pl-4 text-muted-foreground font-normal whitespace-nowrap">
                                        {formatKstTime(trade.created_at)}
                                      </td>
                                      <td className="p-3 whitespace-nowrap">
                                        <Badge className={`font-normal text-[10px] px-2 py-0.5 ${isEntry ? (isLong ? 'bg-emerald-500/10 text-emerald-500' : 'bg-rose-500/10 text-rose-500') : 'bg-amber-500/10 text-amber-500'}`}>
                                          {isEntry ? `ENTRY ${trade.side}` : `EXIT ${trade.side}`}
                                        </Badge>
                                      </td>
                                      <td className="p-3 text-right font-normal whitespace-nowrap">
                                        {Number(trade.price).toLocaleString()} 
                                      </td>
                                      <td className="p-3 text-right font-normal whitespace-nowrap">
                                        {trade.qty}
                                      </td>
                                      <td className="p-3 text-right font-normal whitespace-nowrap">
                                        {!isEntry && trade.pnl !== undefined && trade.pnl !== null ? (
                                          <span className={Number(trade.pnl) >= 0 ? 'text-emerald-500' : 'text-rose-500'}>
                                            {Number(trade.pnl) >= 0 ? '+' : ''}{Number(trade.pnl).toFixed(2)} 
                                            <br />
                                            {(() => {
                                              const pnl = Number(trade.pnl);
                                              const price = Number(trade.price);
                                              const qty = Number(trade.qty);
                                              const leverage = Number(trade.leverage || 1);
                                              const denom = trade.side === 'short' ? price * qty + pnl : price * qty - pnl;
                                              if (!denom || Math.abs(denom) < 0.0001) return null;
                                              const roe = (pnl * leverage / denom) * 100;
                                              const raw = (pnl / denom) * 100;
                                              return <span className="ml-1 text-[11px] opacity-60 font-normal">{roe >= 0 ? '+' : ''}{roe.toFixed(2)}% / {raw >= 0 ? '+' : ''}{raw.toFixed(2)}%</span>;
                                            })()}
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </td>
                                      <td className="p-3 text-right font-normal whitespace-nowrap text-[10px]">
                                        {!isEntry && trade.fee !== undefined && trade.fee !== null ? (
                                          <span className="text-muted-foreground">
                                            {Number(trade.fee).toFixed(4)}
                                          </span>
                                        ) : (
                                          <span className="text-muted-foreground">-</span>
                                        )}
                                      </td>
                                      <td className="p-3 pr-4 text-muted-foreground font-normal max-w-[200px] truncate" title={trade.reason}>
                                        {trade.reason || '-'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>

                          <div className="flex items-center justify-between pt-2 px-2">
                            <div className="text-xs font-bold text-muted-foreground">
                              총 <span className="text-foreground font-black">{tradesData.total.toLocaleString()}</span>건 중{' '}
                              <span className="text-foreground font-black">
                                {Math.min((tradesPage - 1) * tradesPer + 1, tradesData.total)}
                              </span>
                              ~
                              <span className="text-foreground font-black">
                                {Math.min(tradesPage * tradesPer, tradesData.total)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setTradesPage(p => Math.max(1, p - 1))}
                                disabled={tradesPage === 1 || isTradesLoading}
                                className="h-8 px-3 rounded-lg text-xs font-bold"
                              >
                                이전
                              </Button>
                              <div className="flex items-center gap-1 px-2 text-xs font-bold">
                                <span className="bg-primary/10 text-primary px-2.5 py-1 rounded-md font-black">{tradesPage}</span>
                                <span className="text-muted-foreground">/ {Math.ceil(tradesData.total / tradesPer) || 1}</span>
                              </div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setTradesPage(p => Math.min(Math.ceil(tradesData.total / tradesPer), p + 1))}
                                disabled={tradesPage >= Math.ceil(tradesData.total / tradesPer) || isTradesLoading}
                                className="h-8 px-3 rounded-lg text-xs font-bold"
                              >
                                다음
                              </Button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <History className="w-8 h-8 text-muted-foreground opacity-10 mb-2" />
                          <p className="text-xs text-muted-foreground font-medium">매매 내역이 없습니다.</p>
                        </div>
                      )}
                    </CardContent>
                  </TabsContent>

                  <TabsContent value="logs" className="mt-0">
                    <CardContent className="p-0">
                      {isLogsLoading && logsData.logs.length === 0 ? (
                        <div className="flex justify-center items-center py-16">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : logsData.logs.length > 0 ? (
                        <div className="max-h-[400px] overflow-y-auto p-3 space-y-1">
                          {logsData.logs.map((log: any) => (
                            <div key={log.id} className="p-3 text-[11px] md:text-[12px] flex items-center justify-between border-b border-border/20 hover:bg-muted/10 transition-colors">
                              <span className={`font-normal flex-1 ${log.level === 'error' ? 'text-rose-500 font-bold' : (log.level === 'warn' ? 'text-amber-500 font-bold' : 'text-foreground/80')}`}>
                                {log.message}
                              </span>
                              <span className="text-[10px] text-muted-foreground/50 font-medium whitespace-nowrap ml-4">
                                {formatKstTime(log.created_at)}
                              </span>
                            </div>
                          ))}
                          {logsData.total > 0 && (
                            <div className="flex items-center justify-center gap-2 pt-3 pb-1">
                              <button
                                onClick={() => setLogsPage(p => Math.max(1, p - 1))}
                                disabled={logsPage === 1 || isLogsLoading}
                                className="px-3 py-1 text-xs font-bold rounded-lg hover:bg-muted disabled:opacity-30"
                              >‹ 이전</button>
                              <span className="text-xs font-bold text-muted-foreground px-2">{logsPage} / {Math.ceil(logsData.total / 30) || 1}</span>
                              <button
                                onClick={() => setLogsPage(p => p + 1)}
                                disabled={logsPage * 30 >= logsData.total || isLogsLoading}
                                className="px-3 py-1 text-xs font-bold rounded-lg hover:bg-muted disabled:opacity-30"
                              >다음 ›</button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <ScrollText className="w-8 h-8 text-muted-foreground opacity-10 mb-2" />
                          <p className="text-xs text-muted-foreground font-medium">로그가 없습니다.</p>
                        </div>
                      )}
                    </CardContent>
                  </TabsContent>

                  <TabsContent value="scanner" className="mt-0">
                    <CardContent className="p-0">
                      {isScannerLogsLoading && scannerLogs.length === 0 ? (
                        <div className="flex justify-center items-center py-16">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                        </div>
                      ) : scannerLogs.length > 0 ? (
                        <div className="max-h-[400px] overflow-y-auto p-3 space-y-1">
                          {scannerLogs.map((log: any, idx: number) => (
                            <div key={idx} className="p-3 text-[11px] md:text-[12px] flex flex-col border-b border-border/20 hover:bg-muted/10 transition-colors gap-1.5">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-1.5">
                                  <Badge className="font-black text-[9px] bg-purple-500/10 text-purple-600 border border-purple-500/20 uppercase px-1.5 py-0.2">
                                    {log.exchange || 'binance'}
                                  </Badge>
                                  <span className="font-black text-foreground">{log.strategy || 'auto_signal'}</span>
                                </div>
                                <span className="text-[10px] text-muted-foreground/60 font-bold whitespace-nowrap">
                                  {log.timestamp}
                                </span>
                              </div>
                              <div className="flex flex-wrap items-center justify-between text-[11px] font-bold text-muted-foreground">
                                <span>감시: <strong className="text-foreground">{log.scanned_count}</strong>개</span>
                                <span>매칭: <strong className={log.hit_count > 0 ? "text-success font-black" : "text-foreground"}>{log.hit_count}</strong>개</span>
                              </div>
                              {log.hits && log.hits.length > 0 && (
                                <div className="text-[11px] bg-success/5 border border-success/15 rounded-lg p-2 mt-1">
                                  <span className="text-success font-black block text-[10px] uppercase mb-0.5">🚀 포착 시그널 (진입 완료)</span>
                                  <span className="text-foreground/90 font-black">{log.hits}</span>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                          <Activity className="w-8 h-8 text-muted-foreground opacity-10 mb-2 animate-pulse" />
                          <p className="text-xs text-muted-foreground font-medium">스캐너 매칭 이력이 없습니다.</p>
                        </div>
                      )}
                      <div className="p-3 text-[10px] text-muted-foreground/60 border-t border-border/10 text-center font-medium bg-muted/5">
                        * 현재 봇 심볼({bot.symbol})과 일치하는 스캔 이력만 표시됩니다.
                      </div>
                    </CardContent>
                  </TabsContent>
                </Tabs>
              </Card>
            </div>
          </div>
        )}
      </main>
      <BottomNav
        activeTab="bots"
        setActiveTab={() => router.get('/signal')}
        current_user={current_user}
      />

      {current_user?.logout_path && (
        <div className="max-w-md mx-auto mt-4 pb-12 flex flex-col items-center gap-1 opacity-40 hover:opacity-100 transition-opacity">
          {(current_user.name || current_user.email) && (
            <a
              href="/dca#profile"
              className="text-[10px] font-bold text-muted-foreground/60 hover:text-primary transition-colors tracking-tight"
            >
              {current_user.name ? `${current_user.name} (${current_user.email})` : current_user.email}
            </a>
          )}
          <div className="flex items-center gap-3">
            {current_user.is_admin && (
              <a
                href="/admin"
                className="text-[10px] font-black text-muted-foreground/60 hover:text-primary transition-colors uppercase tracking-widest flex items-center gap-1"
              >
                <Shield className="w-3 h-3" /> /admin
              </a>
            )}
            {current_user.is_impersonated && (
              <a
                href="?user_id=clear"
                className="text-[11px] font-black text-amber-500 hover:text-amber-600 transition-colors uppercase tracking-widest"
              >
                돌아가기 (Exit Impersonation)
              </a>
            )}
            <button
              type="button"
              onClick={() => router.delete(current_user.logout_path)}
              className="text-[11px] font-black text-muted-foreground/80 hover:text-danger transition-colors uppercase tracking-widest"
            >
              Sign Out / 로그아웃
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
